import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from './PageHeader';
import { HelpModal } from './HelpModal';
import { Footer } from './Footer';
import { trackScreenView } from '../utils/analytics';

interface HowToUseScreenProps {
  onContinue: () => void;
}

export function HowToUseScreen({ onContinue }: HowToUseScreenProps) {
  const { t } = useTranslation('howToUse');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const hasTrackedScreenView = useRef(false);

  // Get consultation ID from root element
  const consultationId = (() => {
    const rootElement = document.getElementById('consultation-wizard-root');
    return rootElement?.getAttribute('data-consultation-id') || 'unknown';
  })();

  // Track screen view when component mounts (only once)
  useEffect(() => {
    if (!hasTrackedScreenView.current) {
      trackScreenView('how_to_use', consultationId, 2);
      hasTrackedScreenView.current = true;
    }
  }, [consultationId]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <HelpModal 
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
      <PageHeader onHelpClick={() => setShowHelpModal(true)} />
      
      <main role="main" className="flex-1 flex items-start justify-center w-full px-4 py-8">
        <div className="max-w-6xl w-full">
          <div className="text-center mb-8">
            <h1 className="title mb-4">{t('title')}</h1>
            <p className="max-w-2xl mx-auto">
              {t('description')}
            </p>
          </div>

          {/* How It Works - Full Width */}
          <div className="bg-white border border-[#FFD4C7] p-8 mb-8">
            <h2 className="mb-6 text-[24px] font-bold">{t('howItWorks.title')}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF6F4E] text-white flex items-center justify-center font-bold">1</div>
                <div>
                  <h3 className="font-bold mb-2 text-[18px]">{t('howItWorks.step1.title')}</h3>
                  <p className="text-[16px]">{t('howItWorks.step1.description')}</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF6F4E] text-white flex items-center justify-center font-bold">2</div>
                <div>
                  <h3 className="font-bold mb-2 text-[18px]">{t('howItWorks.step2.title')}</h3>
                  <p className="text-[16px]">{t('howItWorks.step2.description')}</p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF6F4E] text-white flex items-center justify-center font-bold">3</div>
                <div>
                  <h3 className="font-bold mb-2 text-[18px]">{t('howItWorks.step3.title')}</h3>
                  <p className="text-[16px]">{t('howItWorks.step3.description')}</p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF6F4E] text-white flex items-center justify-center font-bold">4</div>
                <div>
                  <h3 className="font-bold mb-2 text-[18px]">{t('howItWorks.step4.title')}</h3>
                  <p className="text-[16px]">{t('howItWorks.step4.description')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="bg-[#FFF7F5] border border-[#FF6F4E] p-6 mb-6">
            <h3 className="font-bold mb-2 text-[18px]">{t('importantNote.title')}</h3>
            <p className="text-[16px]">
              {t('importantNote.description')}
            </p>
          </div>

          {/* Continue Button */}
          <div className="text-center">
            <button
              onClick={onContinue}
              className="bg-primary text-primary-foreground px-12 hover:bg-primary/90 transition-colors font-bold text-[24px] inline-flex items-center justify-center gap-2 min-h-[64px]"
            >
              {t('continueButton')}
            </button>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

